// src/components/exam-builder/SpeakingIntegratedModal.jsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";
import MediaPickerModal from "./MediaPickerModal";
import { FolderOpen } from "lucide-react";

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  prompt_text: "",
  prompt_audio_url: "",
  allowPause: false,
  seg_start: "",
  seg_end: "",
  prep_sec: 15,
  resp_sec: 60,
  min_sec: 10,
  max_sec: 60,
  upload_on_stop: true,
  rubric_fa: "",
  rubric_en: "",
  layout: "top-bottom",
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";
  const body = item?.body || {};
  const prompt = body.prompt || {};
  const audio = prompt.audio || {};
  const segment = audio.segment || {};
  const timings = body.timings || {};
  const capture = body.capture || {};
  const rubric = body.rubric || {};
  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    prompt_text: prompt.textHtml || "",
    prompt_audio_url: audio.url || "",
    allowPause: !!audio.allowPause,
    seg_start: typeof segment.startSec === "number" ? segment.startSec : "",
    seg_end: typeof segment.endSec === "number" ? segment.endSec : "",
    prep_sec:
      typeof timings.prepTimeSec === "number" ? timings.prepTimeSec : 15,
    resp_sec:
      typeof timings.responseTimeSec === "number"
        ? timings.responseTimeSec
        : 60,
    min_sec: typeof capture.minSec === "number" ? capture.minSec : 10,
    max_sec: typeof capture.maxSec === "number" ? capture.maxSec : 60,
    upload_on_stop:
      typeof capture.uploadOnStop === "boolean" ? capture.uploadOnStop : true,
    rubric_fa: rubric.fa || "",
    rubric_en: rubric.en || "",
    layout: body.layout || "top-bottom",
    points:
      typeof item.points === "number" && !Number.isNaN(item.points)
        ? item.points
        : 1,
    status: item.status || "published",
    tags: Array.isArray(item.tags) ? item.tags.join(",") : "",
    explanation_fa,
    explanation_en,
  };
};

export default function SpeakingIntegratedModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm());
  const [fileModalOpen, setFileModalOpen] = useState(false);

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  React.useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  if (!isOpen) return null;

  const num = (v, d = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
  };

  const submit = async (e) => {
    e.preventDefault();

    const audioUrl = (form.prompt_audio_url || "").trim();
    if (!audioUrl) {
      toast.error(
        t("speaking_integrated.audio_required", "آدرس صوت پرامپت الزامی است"),
      );
      return;
    }

    const start = form.seg_start !== "" ? num(form.seg_start, 0) : undefined;
    const end = form.seg_end !== "" ? num(form.seg_end, 0) : undefined;
    if (end !== undefined && start !== undefined && !(end > start)) {
      toast.error(
        t("speaking_integrated.bad_segment", "End باید بزرگ‌تر از Start باشد"),
      );
      return;
    }

    const prep = Math.max(0, num(form.prep_sec, 0));
    const resp = Math.max(1, num(form.resp_sec, 60));
    const minS = Math.max(0, num(form.min_sec, 0));
    const maxS = Math.max(1, num(form.max_sec, resp));

    if (minS > maxS) {
      toast.error(
        t("speaking_integrated.min_gt_max", "حداقل زمان از حداکثر بیشتر است"),
      );
      return;
    }
    if (maxS > resp) {
      toast.error(
        t(
          "speaking_integrated.max_gt_resp",
          "حداکثر ضبط نباید از زمان پاسخ بیشتر باشد",
        ),
      );
      return;
    }

    const seg =
      form.seg_start !== "" || form.seg_end !== ""
        ? {
            startSec:
              form.seg_start !== "" ? num(form.seg_start, 0) : undefined,
            endSec: form.seg_end !== "" ? num(form.seg_end, 0) : undefined,
          }
        : undefined;

    const payload = {
      kind: "speaking_integrated",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: {
        fa: form.stem_fa || undefined,
        en: form.stem_en || undefined,
      },
      body: {
        prompt: {
          textHtml: form.prompt_text || "",
          audio: {
            url: audioUrl,
            allowPause: !!form.allowPause,
            segment: seg,
          },
        },
        timings: {
          prepTimeSec: prep,
          responseTimeSec: resp,
        },
        capture: {
          type: "record",
          minSec: minS,
          maxSec: maxS,
          uploadOnStop: form.upload_on_stop !== false,
        },
        rubric: {
          fa: form.rubric_fa || "",
          en: form.rubric_en || "",
        },
        layout: form.layout || "top-bottom",
      },
      points: Math.max(1, num(form.points, 1)),
      status: form.status || "draft",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");
      toast.success(
        isEditMode
          ? t(
              "speaking_integrated.updated",
              "سؤال Speaking (یکپارچه) به‌روزرسانی شد",
            )
          : t(
              "speaking_integrated.created",
              "سؤال Speaking (یکپارچه) ساخته شد",
            ),
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  return (
    <>
      <MediaPickerModal
        isOpen={fileModalOpen}
        onClose={() => setFileModalOpen(false)}
        token={token}
      />
      <form onSubmit={submit} className="space-y-4">
        {/* Stem */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div dir={isRtl ? "rtl" : "ltr"}>
            <label className="block text-sm font-medium mb-1">
              {t("speaking_integrated.stem_fa", "متن سؤال (فارسی)")}
            </label>
            <CustomEditor
              value={form.stem_fa}
              onEditorChange={(content) =>
                setForm((f) => ({ ...f, stem_fa: content }))
              }
              placeholder={t(
                "speaking_integrated.stem_fa_ph",
                "صورت سؤال را وارد کنید",
              )}
              directionality={isRtl ? "rtl" : "ltr"}
              height={120}
            />
          </div>
          <div dir="ltr">
            <label className="block text-sm font-medium mb-1">
              {t("speaking_integrated.stem_en", "Prompt (EN)")}
            </label>
            <CustomEditor
              value={form.stem_en}
              onEditorChange={(content) =>
                setForm((f) => ({ ...f, stem_en: content }))
              }
              placeholder="Enter the prompt in English (optional)"
              directionality="ltr"
              height={120}
            />
          </div>
        </div>

        {/* Prompt text (optional) */}
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t(
              "speaking_integrated.prompt_text",
              "متن راهنما/پرامپت (اختیاری)",
            )}
          </label>
          <CustomEditor
            value={form.prompt_text}
            onEditorChange={(content) =>
              setForm((f) => ({ ...f, prompt_text: content }))
            }
            placeholder={t(
              "speaking_integrated.prompt_text_ph",
              "مثلاً توضیح کار: خلاصه کنید، مقایسه کنید، ...",
            )}
            directionality={isRtl ? "rtl" : "ltr"}
            height={120}
          />
        </div>

        <button
          type="button"
          onClick={() => setFileModalOpen(true)}
          className="px-3 py-2 rounded-lg border bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-2 whitespace-nowrap"
          title={t("media_picker.title", "Browse")}
        >
          <FolderOpen className="w-4 h-4" />
          {t("media_picker.title", "Browse")}
        </button>

        {/* Prompt audio + segment */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">
              {t("speaking_integrated.prompt_audio_url", "آدرس صوت پرامپت *")}
            </label>
            <input
              name="prompt_audio_url"
              className="w-full border rounded-lg px-3 py-2"
              placeholder="https://…/prompt.mp3"
              value={form.prompt_audio_url}
              onChange={onChange}
            />
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2">
              <input
                id="allowPause"
                type="checkbox"
                name="allowPause"
                checked={form.allowPause}
                onChange={onChange}
              />
              <span className="text-sm">
                {t(
                  "speaking_integrated.allow_pause",
                  "اجازه مکث در پخش پرامپت",
                )}
              </span>
            </label>
          </div>
        </div>

        {/* Segment (optional) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("speaking_integrated.seg_start", "شروع ثانیه (اختیاری)")}
            </label>
            <input
              type="number"
              name="seg_start"
              min={0}
              className="w-full border rounded-lg px-3 py-2"
              value={form.seg_start}
              onChange={onChange}
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("speaking_integrated.seg_end", "پایان ثانیه (اختیاری)")}
            </label>
            <input
              type="number"
              name="seg_end"
              min={0}
              className="w-full border rounded-lg px-3 py-2"
              value={form.seg_end}
              onChange={onChange}
              placeholder="مثلاً 45"
            />
          </div>
        </div>

        {/* Timings */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("speaking_integrated.prep_sec", "زمان آماده‌سازی (ثانیه)")}
            </label>
            <input
              type="number"
              name="prep_sec"
              min={0}
              className="w-full border rounded-lg px-3 py-2"
              value={form.prep_sec}
              onChange={onChange}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("speaking_integrated.resp_sec", "زمان پاسخ (ثانیه)")}
            </label>
            <input
              type="number"
              name="resp_sec"
              min={1}
              className="w-full border rounded-lg px-3 py-2"
              value={form.resp_sec}
              onChange={onChange}
            />
          </div>

          {/* Capture */}
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("speaking_integrated.min_sec", "حداقل ضبط (ثانیه)")}
            </label>
            <input
              type="number"
              name="min_sec"
              min={0}
              className="w-full border rounded-lg px-3 py-2"
              value={form.min_sec}
              onChange={onChange}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("speaking_integrated.max_sec", "حداکثر ضبط (ثانیه)")}
            </label>
            <input
              type="number"
              name="max_sec"
              min={1}
              className="w-full border rounded-lg px-3 py-2"
              value={form.max_sec}
              onChange={onChange}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              name="upload_on_stop"
              checked={form.upload_on_stop}
              onChange={onChange}
            />
            <span className="text-sm">
              {t("speaking_integrated.upload_on_stop", "آپلود بعد از ضبط")}
            </span>
          </label>

          <div>
            <label className="block text-sm font-medium mb-1">
              {t("speaking_integrated.layout", "چیدمان")}
            </label>
            <select
              name="layout"
              className="border rounded-lg px-3 py-2"
              value={form.layout}
              onChange={onChange}
            >
              <option value="top-bottom">top-bottom</option>
              <option value="left-right">left-right</option>
              <option value="right-left">right-left</option>
            </select>
          </div>
        </div>

        {/* Rubric (optional) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("speaking_integrated.rubric_fa", "Rubric (FA)")}
            </label>
            <textarea
              className="w-full border rounded-lg px-3 py-2 h-20"
              name="rubric_fa"
              value={form.rubric_fa}
              onChange={onChange}
              placeholder={t(
                "speaking_integrated.rubric_fa_ph",
                "راهنمای تصحیح (اختیاری)",
              )}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Rubric (EN)
            </label>
            <textarea
              className="w-full border rounded-lg px-3 py-2 h-20"
              name="rubric_en"
              value={form.rubric_en}
              onChange={onChange}
              placeholder="Rubric (optional)"
            />
          </div>
        </div>

        {/* Meta */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("speaking_integrated.points", "امتیاز")}
            </label>
            <input
              type="number"
              name="points"
              min={1}
              className="w-full border rounded-lg px-3 py-2"
              value={form.points}
              onChange={onChange}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("speaking_integrated.status", "وضعیت")}
            </label>
            <select
              name="status"
              className="w-full border rounded-lg px-3 py-2"
              value={form.status}
              onChange={onChange}
            >
              <option value="published">{t("common.published")}</option>
              <option value="draft">{t("common.draft")}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("speaking_integrated.tags", "تگ‌ها")}
            </label>
            <input
              name="tags"
              className="w-full border rounded-lg px-3 py-2"
              placeholder={t("common.tags_csv")}
              value={form.tags}
              onChange={onChange}
            />
          </div>
        </div>

        <ExplanationField
          valueFa={form.explanation_fa}
          valueEn={form.explanation_en}
          onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
          onChangeEn={(v) => handleEditorChange("explanation_en", v)}
          isRtl={isRtl}
        />

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded border hover:bg-gray-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            {isEdit ? t("common.update") : t("common.create")}
          </button>
        </div>
      </form>
    </>
  );
}
